import { useQuery } from '@tanstack/react-query';
import { Copy, ShieldCheck } from 'lucide-react';
import { fetchPrototypeAdminAccount } from '../api';
import { qk } from '../api/queryKeys';
import { SectionCard } from './SectionCard';

/** The live app's own first-run status, in words (null = not known yet). */
function adminAccountStatusLabel(setupRequired: boolean | null): string {
  if (setupRequired === true) return 'not set up yet';
  if (setupRequired === false) return 'set up ✓';
  return 'unknown';
}

/**
 * A factory prototype's admin-account claim info: the one-time setup code the
 * first visitor of the app's /admin must enter, and whether the app's owner
 * login exists yet. Renders nothing for app-made or static prototypes.
 */
export function PrototypeAdminAccountCard({ prototypeId }: { prototypeId: string }) {
  const query = useQuery({
    queryKey: qk.prototypeAdminAccount(prototypeId),
    queryFn: () => fetchPrototypeAdminAccount(prototypeId),
  });
  const admin = query.data;
  if (!admin) return null;

  return (
    <SectionCard title="App admin account">
      <div className="space-y-2">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-ink">
          <ShieldCheck className="h-3.5 w-3.5 flex-none text-ink-faint" aria-hidden="true" />
          <span>
            Setup code:{' '}
            <code className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-xs">{admin.setupCode ?? '—'}</code>
          </span>
          {admin.setupCode && (
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => navigator.clipboard?.writeText(admin.setupCode ?? '')}
              title="Copy setup code"
            >
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
              Copy
            </button>
          )}
          <span className="text-ink-faint">·</span>
          <span>Admin account: {adminAccountStatusLabel(admin.setupRequired)}</span>
        </p>
        <p className="text-xs text-ink-muted">
          Whoever opens{' '}
          <a href={admin.url} target="_blank" rel="noreferrer" className="font-mono underline">
            {admin.url}
          </a>{' '}
          first creates the app&apos;s owner login and must enter this code. Send it with the live link; the
          account lives only in the app.
        </p>
      </div>
    </SectionCard>
  );
}
